import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ErrorHandler,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Auth } from '@freelancer/auth';
import { LocalStorage } from '@freelancer/local-storage';
import { ModalService } from '@freelancer/ui';
import {
  Permissions,
  PermissionStatus,
  PermissionType,
} from '@freelancer/ui/permissions';
import { firstValueFrom } from '@freelancer/utils';
import type * as Rx from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { Notifications } from './notifications.service';

/**
 * Manage the push notifications registration, i.e. register/unregister on
 * login/logout
 */
@Component({
  selector: 'fl-notifications',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private notificationRegistrationSubscription?: Rx.Subscription;

  constructor(
    private auth: Auth,
    private errorHandler: ErrorHandler,
    private localStorage: LocalStorage,
    private modalService: ModalService,
    private notifications: Notifications,
    private permissions: Permissions,

    private router: Router,
  ) {}

  ngOnInit() {
    if (this.notifications.isSupported()) {
      // On logout, unregister push notifications
      this.auth.registerBeforeLogoutAction(() =>
        this.notifications.unregisterPushNotifications(),
      );

      this.notificationRegistrationSubscription = this.auth
        .isLoggedIn()
        .pipe(
          switchMap(async isLoggedIn => {
            // If logged-in, try registering for push notifications
            if (!isLoggedIn) {
              this.localStorage.set('notificationsRequested', false);
              // This closes the permissions request modal if the user is
              // logged out. This may be necessary in the case that the user
              // reinstalls the app but has an expired token in the keychain.
              this.modalService.close();
              return;
            }

            // Permission has not been granted yet, do nothing,
            // registerPushNotifications() will be called from the push
            // notifications banner
            const notificationsPermissions =
              await this.permissions.checkPermissions(
                PermissionType.PUSH_NOTIFICATIONS,
              );

            switch (notificationsPermissions) {
              case PermissionStatus.GRANTED:
                break;
              case PermissionStatus.PROMPT:
              case PermissionStatus.PROMPT_WITH_RATIONALE:
                // This is to make sure that we don't try to request permission
                // until the app has finished loading/signing in.
                await firstValueFrom(
                  this.router.events.pipe(
                    filter(
                      event =>
                        event instanceof NavigationEnd &&
                        event.url !== '/internal/pwa' &&
                        event.url !== '/',
                    ),
                  ),
                );
                break;
              case PermissionStatus.DENIED:
              default:
                return;
            }

            // Register push notifications if eligible
            const shouldRegister =
              !(await firstValueFrom(
                this.localStorage.get('notificationsRequested'),
              )) ??
              (await this.notifications.shouldRegisterPushNotifications());
            if (shouldRegister) {
              const registrationResult =
                await this.notifications.registerPushNotifications();
              this.localStorage.set('notificationsRequested', true);
              if (registrationResult.status === 'error') {
                // Log the error as this should not happen as we are checking
                // canShowNotification() first
                const errorMsgString = registrationResult.errorMsg
                  ? `, ${registrationResult.errorMsg}`
                  : '';
                this.errorHandler.handleError(
                  new Error(
                    `registerPushNotifications failed with code ${registrationResult.errorCode}${errorMsgString}`,
                  ),
                );
              }
            }
          }),
        )
        .subscribe();
    }
  }

  ngOnDestroy() {
    if (this.notificationRegistrationSubscription) {
      this.notificationRegistrationSubscription.unsubscribe();
    }
  }
}
